import { useEffect, useRef, useCallback, useState } from 'react'
import { Model } from 'survey-core'
import { Survey } from 'survey-react-ui'
import type { Survey as SurveyDefinition } from '../types/survey'

interface SurveyRendererProps {
  survey: SurveyDefinition
  onComplete: (data: Record<string, unknown>, durationMs: number) => void
  onError?: (error: Error) => void
}

export function SurveyRenderer({ survey, onComplete, onError }: SurveyRendererProps) {
  const startTimeRef = useRef<number>(Date.now())
  const [model, setModel] = useState<Model | null>(null)

  // Initialize (or reinit) the SurveyJS model from the survey definition JSON
  useEffect(() => {
    startTimeRef.current = Date.now()
    const nextModel = new Model(survey.definition)

    // Optional: apply theme / localization
    if (survey.locale) {
      nextModel.locale = survey.locale
    }

    setModel(nextModel)
  }, [survey])

  const handleComplete = useCallback(
    (completedModel: Model) => {
      try {
        const durationMs = Date.now() - startTimeRef.current
        // model.data returns a plain object of question name → answer
        const data = completedModel.data as Record<string, unknown>
        onComplete(data, durationMs)
      } catch (err) {
        onError?.(err instanceof Error ? err : new Error(String(err)))
      }
    },
    [onComplete, onError],
  )

  if (!model) return null

  return (
    <div className="survey-renderer">
      <Survey model={model} onComplete={handleComplete} />
    </div>
  )
}
